import type RouteInfo from '@ember/routing/route-info'
import { RolloverStep, STEP_ROUTE_NAMES } from 'teacher-ui-client/school-rollover/steps'
import { hasMetadataRecord } from 'teacher-ui-client/utils/routing'

function isStep(value: unknown): value is RolloverStep {
  return Object.values(RolloverStep).includes(value as RolloverStep)
}

function isValidStep(step: RolloverStep, steps: RolloverStep[]) {
  return steps.includes(step)
}

function isRolloverStepBeforeOrEqualTo(stepA: RolloverStep, stepB: RolloverStep, steps: RolloverStep[]) {
  const validSteps = isValidStep(stepA, steps) && isValidStep(stepB, steps)
  return validSteps && steps.indexOf(stepA) <= steps.indexOf(stepB)
}

function isStepBeforeEqualToOrNext(stepA: RolloverStep, stepB: RolloverStep, steps: RolloverStep[]) {
  const indexA = steps.indexOf(stepA)
  const indexB = steps.indexOf(stepB)
  const validSteps = isValidStep(stepA, steps) && isValidStep(stepB, steps)
  const isBefore = indexA < indexB
  const isEqualTo = stepA === stepB
  const isNext = indexA === indexB + 1
  return validSteps && (isBefore || isEqualTo || isNext)
}

/**
 * Traverse upwards in the route hierarchy, from the given route info, to find an attribute value defined in
 * the buildRouteInfoMetadata hook of a route.
 *
 * RouteInfo defines a find() method, which is opposite of this (traverses downwards from top to bottom). Using that
 * would not allow us to override metadata in a lower route, for example, if a child route needed to have a more
 * specific title.
 * https://api.emberjs.com/ember/3.22/classes/RouteInfo/methods/find?anchor=find
 */
function _findMetadataFromRouteInfo(routeInfo: Nullable<RouteInfo>, attribute: string): unknown {
  for (let currentRouteInfo = routeInfo; currentRouteInfo; currentRouteInfo = currentRouteInfo?.parent) {
    if (hasMetadataRecord(currentRouteInfo) && attribute in currentRouteInfo.metadata) {
      return currentRouteInfo.metadata[attribute]
    }
  }
}

function getStepFromRouteInfo(routeInfo: Nullable<RouteInfo>): Nullable<RolloverStep> {
  const maybeStep = _findMetadataFromRouteInfo(routeInfo, 'stepId')
  return isStep(maybeStep) ? maybeStep : null
}

function getTitleFromRouteInfo(routeInfo: Nullable<RouteInfo>): Nullable<string> {
  const maybeTitle = _findMetadataFromRouteInfo(routeInfo, 'title')
  return typeof maybeTitle === 'string' ? maybeTitle : null
}

function getHideFooterFromRouteInfo(routeInfo: Nullable<RouteInfo>): boolean {
  return _findMetadataFromRouteInfo(routeInfo, 'hideFooter') === true
}

function getRouteLocalNameForStep(step: RolloverStep) {
  return STEP_ROUTE_NAMES[step]
}

function getRouteNameForStep(step: RolloverStep, parentRoute = 'manage.rollover.steps') {
  return `${parentRoute}.${getRouteLocalNameForStep(step)}`
}

function isFirstStep(step: RolloverStep, steps: RolloverStep[]) {
  return steps.indexOf(step) === 0
}

function isLastStep(step: RolloverStep, steps: RolloverStep[]) {
  return steps.indexOf(step) === steps.length - 1
}

function getPreviousStepIndex(step: RolloverStep, steps: RolloverStep[]) {
  if (isValidStep(step, steps) && !isFirstStep(step, steps)) {
    return steps.indexOf(step) - 1
  } else {
    return undefined
  }
}

function getNextStepIndex(step: RolloverStep, steps: RolloverStep[]) {
  if (isValidStep(step, steps) && !isLastStep(step, steps)) {
    return steps.indexOf(step) + 1
  } else {
    return undefined
  }
}

function getPreviousStep(step: RolloverStep, steps: RolloverStep[]) {
  const stepIndex = getPreviousStepIndex(step, steps)
  return typeof stepIndex === 'number' ? steps[stepIndex] : undefined
}

function getNextStep(step: RolloverStep, steps: RolloverStep[]) {
  const stepIndex = getNextStepIndex(step, steps)
  return typeof stepIndex === 'number' ? steps[stepIndex] : undefined
}

export default {
  isStep,
  isValidStep,
  isRolloverStepBeforeOrEqualTo,
  isStepBeforeEqualToOrNext,
  getStepFromRouteInfo,
  getTitleFromRouteInfo,
  getHideFooterFromRouteInfo,
  getRouteNameForStep,
  getRouteLocalNameForStep,
  isFirstStep,
  isLastStep,
  getNextStepIndex,
  getNextStep,
  getPreviousStepIndex,
  getPreviousStep,
  _findMetadataFromRouteInfo,
}
